import { cariSlot, type KunciSlot } from "./slot"

export interface AsetMedia {
  url: string
  lebar: number
  tinggi: number
  diubahAt: string
}

export type PetaAsetMedia = Partial<Record<KunciSlot, AsetMedia>>

export const HOST_UNGGAHAN_DIIZINKAN = ["cdn.zass.in"]
const BATAS_DIMENSI = { min: 16, maks: 4096 } as const

const JALUR_PUBLIK = /^\/(?!\/)[A-Za-z0-9._~\-/]+\.(?:png|webp|jpe?g|gif|svg|avif)$/i

export function jalurPublikValid(jalur: string): boolean {
  return JALUR_PUBLIK.test(jalur) && !jalur.split("/").includes("..")
}

export type HasilAset = { ok: true; data: AsetMedia } | { ok: false; error: string }

export function rapikanAset(masukan: { url: string; lebar: number; tinggi: number }, sekarang: Date = new Date()): HasilAset {
  const mentah = masukan.url.trim()
  const lokal = mentah.startsWith("/")
  if (lokal && !jalurPublikValid(mentah)) return { ok: false, error: "Jalur file di folder public tidak valid (contoh: /maskot/404.webp)." }

  let url: URL
  try {
    url = lokal ? new URL(mentah, "https://situs.invalid") : new URL(mentah)
  } catch {
    return { ok: false, error: "Alamat gambar tidak valid." }
  }
  if (!lokal && url.protocol !== "https:") return { ok: false, error: "Alamat gambar harus memakai https." }
  if (!lokal && !HOST_UNGGAHAN_DIIZINKAN.includes(url.hostname)) return { ok: false, error: "Gambar harus diunggah lewat pengunggah situs atau berupa jalur di folder public." }

  const lebar = Math.round(masukan.lebar)
  const tinggi = Math.round(masukan.tinggi)
  const wajar = (nilai: number) => Number.isFinite(nilai) && nilai >= BATAS_DIMENSI.min && nilai <= BATAS_DIMENSI.maks
  if (!wajar(lebar) || !wajar(tinggi)) return { ok: false, error: "Ukuran gambar tidak terbaca." }

  const waktu = Number.isNaN(sekarang.getTime()) ? new Date() : sekarang
  return { ok: true, data: { url: lokal ? mentah : url.toString(), lebar, tinggi, diubahAt: waktu.toISOString() } }
}

export function rapikanPetaAset(mentah: unknown): PetaAsetMedia {
  if (!mentah || typeof mentah !== "object") return {}
  const hasil: PetaAsetMedia = {}
  for (const [kunci, nilai] of Object.entries(mentah as Record<string, unknown>)) {
    const slot = cariSlot(kunci)
    if (!slot || !nilai || typeof nilai !== "object") continue
    const sumber = nilai as Record<string, unknown>
    const valid = rapikanAset({ url: String(sumber.url ?? ""), lebar: Number(sumber.lebar), tinggi: Number(sumber.tinggi) }, new Date(String(sumber.diubahAt ?? "")))
    if (valid.ok) hasil[slot.kunci] = { ...valid.data, diubahAt: typeof sumber.diubahAt === "string" ? sumber.diubahAt : valid.data.diubahAt }
  }
  return hasil
}

export function urlDenganVersi(aset: AsetMedia): string {
  const versi = Date.parse(aset.diubahAt)
  if (!Number.isFinite(versi)) return aset.url
  if (aset.url.startsWith("/")) return `${aset.url}?v=${versi}`
  const url = new URL(aset.url)
  url.searchParams.set("v", String(versi))
  return url.toString()
}

export function selisihRasio(aset: Pick<AsetMedia, "lebar" | "tinggi">, lebarIdeal: number, tinggiIdeal: number): number {
  return Math.abs(aset.lebar / aset.tinggi - lebarIdeal / tinggiIdeal)
}
